import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import Image from "next/image";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, Field, ErrorMessage } from "formik";

import { PHONE_REGEX } from "@/utils/common";
import { IMAGE_TYPE, serviceTypeOptions } from "@/utils/constants";
import apiService from "@/common/services/apiService";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";

// Dynamic imports
const FormInput = dynamic(() => import("./FormInput"));
const Button = dynamic(() => import("@/Components/Button"));
const FileUploader = dynamic(() => import("./FileUploader"));
const ServiceSelect = dynamic(() => import("./ServiceSelect"));
const BudgetSelector = dynamic(() => import("./BudgetSelector"), {
  ssr: false,
});

interface QuoteFormValues {
  email: string;
  budget: string;
  contact_on: string;
  country_name: string;
  full_name: string;
  file: File | null;
  service_type: string[];
  ndaSigned: boolean;
  confidential: boolean;
  project_summary: string;
}

const ErrorMessageComponent = (props: any) => (
  <div className="text-red-500 font-medium text-xs mt-2" role="alert">
    {props.children}
  </div>
);

interface QuoteFormProps {
  data?: any;
}

const QuoteForm: React.FC<QuoteFormProps> = ({ data }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [budget, setBudget] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;
  const [value, setBudgetValue] = useState([5000, 150000]);
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);

  // The reCAPTCHA widget pulls ~345KB from gstatic as soon as it mounts. This
  // form sits well below the fold and the widget is invisible (it only runs on
  // submit), so mounting it immediately costs every visitor that download even
  // if they never scroll this far. Mount it once the form comes within range
  // instead: it is ready long before anyone can submit, and nothing moves on
  // screen when it appears.
  const formRef = useRef<HTMLDivElement>(null);
  const [captchaReady, setCaptchaReady] = useState(false);

  useEffect(() => {
    if (captchaReady) return;
    const el = formRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setCaptchaReady(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setCaptchaReady(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [captchaReady]);

  const initialValues: QuoteFormValues = {
    email: "",
    file: null,
    budget: "",
    full_name: "",
    contact_on: "",
    ndaSigned: true,
    country_name: "",
    confidential: true,
    project_summary: "",
    service_type: ["Web Development"],
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().trim().required("Full name is required."),
    email: Yup.string()
      .trim()
      .email("Email is not valid.")
      .max(100, "Email is too long")
      .required("Email is required.")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
    contact_on: Yup.string()
      .nullable()
      .required("Phone is required")
      .matches(PHONE_REGEX, "Invalid phone number")
      .matches(/^[0-9]{10,15}$/, "Phone number must be between 10-15 digits"),
    country_name: Yup.string().required("Country is required."),
    project_summary: Yup.string().required("Project description is required"),
    budget: Yup.string().required("Budget is required"),
    service_type: Yup.array()
      .required("Service type is required")
      .test("length", "Select at least one type of service", (value) => {
        if (!value || value?.length == 0) return false;
        return true;
      }),
    // .required("File is required")
    file: Yup.mixed()
      .nullable()
      .test(
        "type",
        "Only the following formats are accepted: .jpeg, .jpg, .png, .bmp, .pdf and .doc",
        () => {
          let flag = true;
          if (selectedFiles && selectedFiles?.length > 0) {
            flag = false;
            for (let i = 0; i < selectedFiles?.length; i++) {
              if (IMAGE_TYPE.includes(selectedFiles[i]?.type)) flag = true;
              else {
                flag = false;
                break;
              }
            }
          }
          return flag;
        }
      ),
    // project_budget_min: Yup.number().positive().integer().required(),
    // project_budget_max: Yup.number().positive().integer().required(),
  });

  const handleBudget = (val: string, setFieldValue: any) => {
    setBudget(val);
    setFieldValue("budget", val);
    const budget = val.replaceAll("$", "").replaceAll("K", "000");
    setBudgetValue(budget.split("-").map((item: string) => Number(item)));
  };

  // The widget is mounted lazily (see captchaReady above), so on submit make
  // sure it exists before executing. Returning null rather than hanging matters:
  // the previous version resolved only inside `.then`, so if the widget was ever
  // missing the promise never settled and the form stayed stuck on "loading".
  const waitForCaptcha = async (timeoutMs = 5000) => {
    if (reCaptchaRef.current) return reCaptchaRef.current;
    setCaptchaReady(true);
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      if (reCaptchaRef.current) return reCaptchaRef.current;
    }
    return null;
  };

  const validateCaptchaV3 = async () => {
    const captcha = await waitForCaptcha();
    if (!captcha) return null;
    return captcha.executeAsync();
  };

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm, setSubmitting }: any
  ) => {
    try {
      setIsLoading(true);
      const isHuman = await validateCaptchaV3();
      if (!isHuman) return false;
      dispatch(showFormLoader());
      const payload = { ...values, file, budget } as Record<string, any>;
      data.service_type = data?.services?.toString() ?? "";
      const form_data = new FormData();
      for (const key in payload) {
        if (key === "file") {
          selectedFiles.forEach((file) => {
            form_data.append("project_files", file);
          });
        } else if (payload[key])
          form_data.append(key, payload[key]?.toString());
      }
      form_data.append("page_link", window.location.href);
      form_data.append("captcha_token_v3", isHuman as string);
      const page_referer = sessionStorage.getItem("rfp-referer");
      form_data.append("page_title", document.title ?? "Trootech");
      form_data.append("project_budget_min", value[0].toString() ?? "-");
      form_data.append("project_budget_max", value[1].toString() ?? "-");
      if (!!page_referer) {
        form_data.append("page_referer", page_referer);
        sessionStorage.removeItem("rfp-referer");
      }
      const lst: any = await apiService.multipart_post("proposal", form_data);
      if (lst?.data?.success) {
        dispatch(hideFormLoader());
        setBudgetValue([500, 150000]);
        setSelectedFiles([]);
        resetForm();
        setSubmitting(false);
        reCaptchaRef?.current?.reset();
        router.push("/thank-you");
      } else toast.error(lst?.data?.message ?? "Something went wrong");
    } catch (error) {
      console.log("handleSubmit ~ error:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 4000);
    }
  };

  return (
    <div
      ref={formRef}
      className="sm:bg-[url(/images/Quote-bg.webp)] bg-[url(/images/bg-mobile-quote.png)] bg-no-repeat bg-cover bg-center"
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, values }) => (
          <Form
            className="xl:pt-12 xl:px-12 xl:pb-6 md:px-8 md:pt-8 md:pb-[50px] px-4 pt-6 pb-6 h-full items-stretch"
            aria-label="Request for Proposal Form"
          >
            <h3 className="text-heading-5 font-bold text-primary-text text-center sm:text-start">
              Request for Proposal
            </h3>

            <div className="grid lg:grid-cols-2 gap-4 md:mt-6 mt-4 h-full items-stretch">
              <div className="grid lg:grid-cols-2 gap-4 h-full items-stretch">
                <FormInput name="full_name" placeholder="Full name" required />
                <FormInput
                  name="email"
                  type="email"
                  placeholder="Email"
                  required
                />
                <FormInput
                  name="contact_on"
                  type="tel"
                  placeholder="Phone number"
                  required
                />
                <FormInput name="country_name" placeholder="Country" required />

                <div className="textarea input-group sm:col-span-2 mb-0 ">
                  <Field
                    as="textarea"
                    name="project_summary"
                    id="project_summary"
                    className="input h-28 min-h-[120px]"
                    placeholder=" "
                    aria-label="Project description"
                    aria-describedby="project_summary-error"
                    aria-required="true"
                  />
                  <label
                    htmlFor="project_summary"
                    className="text-subtitle-5 font-regular"
                  >
                    Project description*
                  </label>
                  <ErrorMessage
                    name="project_summary"
                    component={ErrorMessageComponent}
                    id="project_summary-error"
                  />
                </div>

                <div className="sm:col-span-2">
                  <ServiceSelect
                    options={serviceTypeOptions.map((item) => item.label)}
                    selectedServices={values.service_type}
                    onChange={(services: string[]) =>
                      setFieldValue("service_type", services)
                    }
                  />
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col h-full">
                  <BudgetSelector
                    selected={budget}
                    onSelect={(val) => handleBudget(val, setFieldValue)}
                  />
                  <ErrorMessage
                    name="budget"
                    component={ErrorMessageComponent}
                    id="budget-error"
                  />
                </div>

                <div className="flex flex-col h-full">
                  <FileUploader
                    file={file}
                    onFileChange={(val) => {
                      setFile(val);
                      setSelectedFiles([val]);
                      setFieldValue("file", val);
                    }}
                  />
                  <ErrorMessage
                    name="file"
                    component={ErrorMessageComponent}
                    id="file-error"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-8 md:w-auto w-full">
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    100% confidential
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Image
                    src="/images/confidential.svg"
                    alt="confidential"
                    width={18}
                    height={18}
                  />
                  <span className="text-primary-text text-subtitle-4 font-regular">
                    We sign NDA
                  </span>
                </div>
              </div>
              <Button
                type="submit"
                variant="small"
                className="my-0"
                loading={isLoading}
                label={data?.button_text || "Submit"}
              />
            </div>
            {sitekey && captchaReady && (
              <ReCAPTCHA
                style={{ display: "inline" }}
                theme="dark"
                size="invisible"
                ref={reCaptchaRef}
                sitekey={sitekey}
              />
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};
export default QuoteForm;